import {zodResolver} from '@hookform/resolvers/zod';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import {useSubtractWarehouseItemQuantityMutation} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import type {WarehouseItemStockUpdateFormValues} from '../utils/warehouseFormInputs';
import {
  mapWarehouseItemStockUpdateValuesToChangeQuantityInput,
  warehouseItemStockUpdateFormDefaultValues,
  warehouseItemStockUpdateFormSchema,
} from '../utils/warehouseFormInputs';
import SharedWarehouseItemStockUpdateFormFields from './WarehouseItemStockUpdateFormFields';

export type UpdateSharedWarehouseItemStockDialogProps = DialogProps;

const UpdateSharedWarehouseItemStockDialog = ({
  onOpenChange,
  ...props
}: UpdateSharedWarehouseItemStockDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();

  const onSuccess = () => {
    queryClient.invalidateQueries({queryKey: ['GetWarehouseItems']});
    queryClient.invalidateQueries({queryKey: ['GetWarehouseItemById']});
  };

  const subtractWarehouseItemQuantity =
    useSubtractWarehouseItemQuantityMutation(graphqlClient, {onSuccess});

  const form = useForm<WarehouseItemStockUpdateFormValues>({
    resolver: zodResolver(warehouseItemStockUpdateFormSchema),
    defaultValues: warehouseItemStockUpdateFormDefaultValues(),
  });

  const handleSubmit = async (values: WarehouseItemStockUpdateFormValues) => {
    const input =
      mapWarehouseItemStockUpdateValuesToChangeQuantityInput(values);

    try {
      await subtractWarehouseItemQuantity.mutateAsync({input});

      toast({
        title: t('Product updated'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const isDisabled = subtractWarehouseItemQuantity.isPending;

  const warehouseId = form.watch('warehouseId');
  const id = form.watch('id');

  useEffect(() => {
    if (!form.formState.isDirty) return;

    if (!warehouseId) {
      form.setValue('id', '');
      form.setValue('amount', 0);
    }

    if (!id) {
      form.setValue('amount', 0);
    }
  }, [warehouseId, id, form]);

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader className="flex flex-col">
              <DialogTitle className="flex flex-col">
                {t('Pick from warehouse')}
              </DialogTitle>
            </DialogHeader>
            <DialogSection>
              <SharedWarehouseItemStockUpdateFormFields form={form} />
            </DialogSection>
            <DialogFooter>
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => onOpenChange?.(false)}
                >
                  {t('Close')}
                </Button>
                <Button variant="primary" type="submit" disabled={isDisabled}>
                  {t('Submit')}
                </Button>
              </>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default UpdateSharedWarehouseItemStockDialog;
